import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import { setLoadingActions } from "@/reducers/mrgReducer/mrgActions";
import { TokenTypes } from "@/types/token.type";

import store from "../store";

import HungerApi from "./hunger.api";

type CustomRequestConfig = {
  register: boolean;
  loading: boolean;
  queueIndex: number;
};

export type HelperConfig = AxiosRequestConfig & CustomRequestConfig;

type Request = {
  loading: boolean;
  retry: number;
  config: AxiosRequestConfig;
};

type InLineRequest = {
  [key: number]: Request;
};

interface HelperResponse extends AxiosResponse {
  config: HelperConfig;
}

interface HelperError extends AxiosError {
  config: HelperConfig;
}

class apiHelper {
  private IN_LINE_REQUEST: InLineRequest;
  private REQUEST_INDEX: number;
  private IS_LOADING: boolean;

  constructor() {
    this.IN_LINE_REQUEST = [];
    this.REQUEST_INDEX = 0;
    this.IS_LOADING = false;
  }

  loadingChecker = () => {
    let needLoading = false;
    Object.keys(this.IN_LINE_REQUEST).forEach((key: any) => {
      if (this.IN_LINE_REQUEST[key]?.loading) {
        needLoading = true;
      }
    });

    return needLoading;
  };

  settingLoadingStatus = (status: boolean) => {
    if (status === this.IS_LOADING) return;
    this.IS_LOADING = status;

    store.dispatch(setLoadingActions(this.IS_LOADING));
  };

  isRegisted = (config: HelperConfig) => {
    const { register } = config;
    // noinspection RedundantConditionalExpressionJS
    return register ? true : false;
  };

  register = (config: HelperConfig) => {
    if (this.isRegisted(config)) return config;

    const loading = config?.loading === undefined ? true : config.loading;
    const queueIndex = this.REQUEST_INDEX;
    const newConfig = { ...config, queueIndex, loading, register: true };
    this.IN_LINE_REQUEST[this.REQUEST_INDEX] = {
      loading,
      retry: 0,
      config: newConfig,
    };

    this.REQUEST_INDEX++;
    return newConfig;
  };

  unRegister = (config: HelperConfig) => {
    const { queueIndex } = config;

    delete this.IN_LINE_REQUEST[queueIndex];
  };

  handleRequest = (config: HelperConfig | AxiosRequestConfig) => {
    const newConfig = this.register(config as HelperConfig);
    const isLoading = this.loadingChecker();
    this.settingLoadingStatus(isLoading);

    const token = localStorage.getItem(TokenTypes.ACCESS_TOKEN);

    if (token) {
      newConfig.headers["X-HUNGER-MGT-TOKEN"] = token;
    }
    newConfig.headers["X-HUNGER-API-VERSION"] = "20220901";

    return {
      ...newConfig,
    };
  };

  handleResponse = (response: HelperResponse | AxiosResponse) => {
    const { config } = response;
    this.unRegister(config as HelperConfig);

    const isLoading = this.loadingChecker();
    this.settingLoadingStatus(isLoading);

    return response;
  };

  isNeedRetry = (index: number) => {
    if (this.IN_LINE_REQUEST[index].retry < 5) {
      this.IN_LINE_REQUEST[index].retry++;
      return true;
    } else {
      return false;
    }
  };

  handleRequestError = (error: HelperError) => {
    return new Promise(async (resolve, reject) => {
      if (axios.isCancel(error)) {
        console.log("取消請求");
      } else {
        try {
          const { config } = error;
          const { queueIndex } = config;

          if (this.isNeedRetry(queueIndex) && error.response === undefined) {
            const res = await HungerApi.retry(config);
            resolve(res);
          } else {
            this.unRegister(config);
            const isLoading = this.loadingChecker();
            this.settingLoadingStatus(isLoading);
            reject(error);
          }
        } catch (err) {
          reject(err);
        }
      }
    });
  };
}

export default new apiHelper();
